import * as React from "react";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import {
  IgnoreZone,
  SiteCamerasResult,
} from "./SiteAboutPageUtils/siteAboutInterfaces";
import { useFlexIgnoreZonesOnboardingStore } from "./FlexIgnoreZonesOnboarding/flexIgnoreZonesOnboardingStore";
import {
  EMPTY_FLEX_PRESETS_LIST,
  FLEX_IZ_LD_EMPTY_CAMERA_CONTEXTS,
  FLEX_IZ_LD_EMPTY_PRESETS,
  flexIgnoreZonesNoopLoadCameraContext,
} from "./FlexIgnoreZonesOnboarding/flexIgnoreZonesLdEmpty";
import { FlexIgnoreZonesNewPresetDialog } from "./FlexIgnoreZonesOnboarding/FlexIgnoreZonesNewPresetDialog";
import {
  FLEX_IZ_CREATE_NEW_PRESET_OPTION,
  FLEX_IZ_DEFAULT_CAMERA_CONFIG_LABEL,
  FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION,
  FLEX_IZ_NO_IGNORE_ZONES_OPTION,
} from "./FlexIgnoreZonesOnboarding/flexIzPresetSelectConstants";
import {
  collapseNoIgnoreZonesPresetOptions,
  filterFlexPresetsForApiPresetDropdown,
  normalizeFlexPresetSelectionToApiBackedMenuValue,
} from "./FlexIgnoreZonesOnboarding/flexPresetDisplayDedupe";

export function useFlexIgnoreZonesCameraRow({
  siteInfo,
  obj,
  expanded,
  visibleIgnoreZones,
  allIgnoreZones,
}: {
  siteInfo: { id?: number | string; has_flex_schedule?: boolean };
  obj: SiteCamerasResult;
  expanded: boolean;
  visibleIgnoreZones: IgnoreZone[];
  allIgnoreZones: IgnoreZone[];
}) {
  const { isFeatureEnabled } = useFeatureFlag();
  const showFlexIgnoreZones = isFeatureEnabled("showFlexIgnoreZones", false);
  const siteIdNum = React.useMemo(() => {
    const raw = siteInfo?.id;
    if (raw === undefined || raw === null || raw === "") {
      return NaN;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : NaN;
  }, [siteInfo?.id]);
  const flexIgnoreZonesEnabledForSite = useFlexIgnoreZonesOnboardingStore(
    (state) =>
      showFlexIgnoreZones &&
      Number.isFinite(siteIdNum) &&
      state.enabledBySite[siteIdNum] === true
  );
  const hasFlexSchedule = !!siteInfo?.has_flex_schedule;
  // Require flex scheduling on the site; onboarding sync (`enabledBySite`) alone is not enough.
  const flexIgnoreZonesEnabled = flexIgnoreZonesEnabledForSite && hasFlexSchedule;
  const flexIzActive = flexIgnoreZonesEnabled;

  const loadCameraContext = useFlexIgnoreZonesOnboardingStore((state) =>
    showFlexIgnoreZones ? state.loadCameraContext : flexIgnoreZonesNoopLoadCameraContext
  );
  const presetsByCamera = useFlexIgnoreZonesOnboardingStore((state) =>
    showFlexIgnoreZones ? state.presetsByCamera : FLEX_IZ_LD_EMPTY_PRESETS
  );
  const cameraContexts = useFlexIgnoreZonesOnboardingStore((state) =>
    showFlexIgnoreZones ? state.cameraContexts : FLEX_IZ_LD_EMPTY_CAMERA_CONTEXTS
  );
  const createCustomPresetDraft = useFlexIgnoreZonesOnboardingStore(
    (state) => state.createCustomPresetDraft
  );
  const removeLocalPresetDraft = useFlexIgnoreZonesOnboardingStore(
    (state) => state.removeLocalPresetDraft
  );

  const [selectedFlexPresetLocalId, setSelectedFlexPresetLocalId] =
    React.useState("");
  const [newPresetDialog, setNewPresetDialog] = React.useState<{
    cameraId: number;
    presetLocalId: string;
    cameraName: string;
  } | null>(null);

  React.useEffect(() => {
    if (!expanded || !flexIzActive || !obj.id) {
      return;
    }
    loadCameraContext(obj.id);
  }, [expanded, flexIzActive, obj.id, loadCameraContext]);

  const flexPresetOptionsForCamera = React.useMemo(() => {
    const all = presetsByCamera[obj.id] ?? EMPTY_FLEX_PRESETS_LIST;
    return collapseNoIgnoreZonesPresetOptions(
      filterFlexPresetsForApiPresetDropdown(all),
      selectedFlexPresetLocalId
    );
  }, [obj.id, presetsByCamera, selectedFlexPresetLocalId]);

  React.useEffect(() => {
    if (!flexIzActive) {
      setSelectedFlexPresetLocalId((prev) => (prev === "" ? prev : ""));
      return;
    }
    // "Default" (camera-level zones + default lines) is always available and is
    // the initial preview selection — never a preset the operator did not pick.
    if (
      selectedFlexPresetLocalId === "" ||
      selectedFlexPresetLocalId === FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION
    ) {
      if (selectedFlexPresetLocalId === "") {
        setSelectedFlexPresetLocalId(FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION);
      }
      return;
    }
    const all = presetsByCamera[obj.id] ?? EMPTY_FLEX_PRESETS_LIST;
    const options = flexPresetOptionsForCamera;
    const normalized = normalizeFlexPresetSelectionToApiBackedMenuValue(
      obj.id,
      all,
      selectedFlexPresetLocalId
    );
    if (
      normalized !== selectedFlexPresetLocalId &&
      options.some((p) => p.localId === normalized)
    ) {
      setSelectedFlexPresetLocalId((prev) =>
        prev === normalized ? prev : normalized
      );
      return;
    }
    const valid = options.some((p) => p.localId === selectedFlexPresetLocalId);
    if (!valid) {
      // A stale selection (deleted preset, discarded draft) falls back to Default,
      // not to whichever preset happens to be first in the list.
      setSelectedFlexPresetLocalId(FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION);
    }
  }, [
    flexIzActive,
    obj.id,
    presetsByCamera,
    selectedFlexPresetLocalId,
    flexPresetOptionsForCamera,
  ]);

  const zonesForPreview = React.useMemo((): IgnoreZone[] => {
    if (!flexIzActive || !selectedFlexPresetLocalId) {
      return visibleIgnoreZones;
    }
    const presets = presetsByCamera[obj.id] ?? EMPTY_FLEX_PRESETS_LIST;
    const preset =
      selectedFlexPresetLocalId === FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION
        ? undefined
        : presets.find((p) => p.localId === selectedFlexPresetLocalId);
    const defaultZones = cameraContexts[obj.id]?.defaultZones ?? [];
    if (!preset) {
      // Default: the camera's own zones.
      return defaultZones.length > 0 ? defaultZones : visibleIgnoreZones;
    }
    if (preset.localId === FLEX_IZ_NO_IGNORE_ZONES_OPTION) {
      return [];
    }
    if (preset.zones && preset.zones.length > 0) {
      return preset.zones;
    }
    return [];
  }, [
    flexIzActive,
    selectedFlexPresetLocalId,
    presetsByCamera,
    cameraContexts,
    obj.id,
    visibleIgnoreZones,
  ]);

  const zonesForColorSidebar = React.useMemo((): IgnoreZone[] => {
    if (!flexIzActive) {
      return allIgnoreZones;
    }
    const defaultZones = cameraContexts[obj.id]?.defaultZones ?? [];
    if (defaultZones.length > 0) {
      return defaultZones;
    }
    return allIgnoreZones;
  }, [flexIzActive, allIgnoreZones, cameraContexts, obj.id]);

  /**
   * Persisted id of the preset the picker shows, or null for Default / a draft
   * that is not saved yet. Drives which line crossings the row previews
   * (`?camera=&preset=` vs `preset__isnull=true`) and which configuration the
   * "Edit Line Crossings" link opens (ENG-1610).
   */
  const selectedFlexPresetId = React.useMemo((): number | null => {
    if (
      !flexIzActive ||
      !selectedFlexPresetLocalId ||
      selectedFlexPresetLocalId === FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION ||
      selectedFlexPresetLocalId === FLEX_IZ_NO_IGNORE_ZONES_OPTION
    ) {
      return null;
    }
    const presets = presetsByCamera[obj.id] ?? EMPTY_FLEX_PRESETS_LIST;
    const preset = presets.find((p) => p.localId === selectedFlexPresetLocalId);
    const persistedId = preset?.persistedId;
    return persistedId != null && Number.isFinite(Number(persistedId))
      ? Number(persistedId)
      : null;
  }, [flexIzActive, selectedFlexPresetLocalId, presetsByCamera, obj.id]);

  /** Name of the named preset the picker shows (null for Default / none). */
  const selectedFlexPresetName = React.useMemo((): string | null => {
    if (
      !flexIzActive ||
      !selectedFlexPresetLocalId ||
      selectedFlexPresetLocalId === FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION
    ) {
      return null;
    }
    const presets = presetsByCamera[obj.id] ?? EMPTY_FLEX_PRESETS_LIST;
    return (
      presets.find((p) => p.localId === selectedFlexPresetLocalId)?.name ?? null
    );
  }, [flexIzActive, selectedFlexPresetLocalId, presetsByCamera, obj.id]);

  const ignoreZonesTitleText = flexIzActive ? "Flex Ignore Zones" : "Ignore Zones";
  const ignoreZonesHeadingClassName =
    "font-mulish text-[16px] text-actuate-blue";

  const flexPresetsForCamera = flexPresetOptionsForCamera;
  const flexCameraCtx = cameraContexts[obj.id];

  const flexPresetSelectControl =
    flexIzActive && flexCameraCtx?.loading ? (
      <p className="font-mulish text-[12px] text-gray-600 py-[4px]">
        Loading presets…
      </p>
    ) : flexIzActive ? (
      <FormControl
        fullWidth
        size="small"
        sx={{ maxWidth: 360, mt: "4px", mb: "8px" }}
      >
        <InputLabel
          id={`flex-iz-preset-label-${obj.id}`}
          sx={{ fontFamily: "Mulish" }}
        >
          Preset
        </InputLabel>
        <Select
          labelId={`flex-iz-preset-label-${obj.id}`}
          label="Preset"
          value={selectedFlexPresetLocalId || ""}
          onChange={(event) => {
            const nextValue = String(event.target.value);
            if (nextValue === FLEX_IZ_CREATE_NEW_PRESET_OPTION) {
              const ctxZones = cameraContexts[obj.id]?.defaultZones;
              const zones =
                ctxZones && ctxZones.length > 0 ? ctxZones : allIgnoreZones;
              const localId = createCustomPresetDraft(obj.id, zones);
              setNewPresetDialog({
                cameraId: obj.id,
                presetLocalId: localId,
                cameraName: obj.camera_name,
              });
              loadCameraContext(obj.id);
              return;
            }
            setSelectedFlexPresetLocalId(nextValue);
          }}
          sx={{ fontFamily: "Mulish" }}
        >
          <MenuItem
            value={FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION}
            sx={{ fontFamily: "Mulish" }}
          >
            {FLEX_IZ_DEFAULT_CAMERA_CONFIG_LABEL}
          </MenuItem>
          {flexPresetsForCamera.map((preset) => (
            <MenuItem
              key={preset.localId}
              value={preset.localId}
              sx={{ fontFamily: "Mulish" }}
            >
              {preset.name}
            </MenuItem>
          ))}
          <MenuItem
            value={FLEX_IZ_CREATE_NEW_PRESET_OPTION}
            sx={{
              color: "#FF8400",
              fontFamily: "Mulish",
              fontWeight: 700,
            }}
          >
            + CREATE NEW PRESET
          </MenuItem>
        </Select>
        <FormHelperText
          sx={{ fontFamily: "Mulish", mx: 0 }}
          data-testid={`flex-iz-preset-helper-${obj.id}`}
        >
          {selectedFlexPresetName != null
            ? `Showing the zones and line crossings of “${selectedFlexPresetName}”. Lines and zones from other configurations are not shown.`
            : "Showing the camera's own zones and default line crossings."}
        </FormHelperText>
      </FormControl>
    ) : null;

  const flexPresetDialog = showFlexIgnoreZones ? (
    <FlexIgnoreZonesNewPresetDialog
      open={newPresetDialog != null}
      cameraId={newPresetDialog?.cameraId ?? 0}
      presetLocalId={newPresetDialog?.presetLocalId ?? null}
      cameraName={newPresetDialog?.cameraName ?? ""}
      assignToSchedule={false}
      onClose={(reason, meta) => {
        if (reason === "submitted" && newPresetDialog) {
          setSelectedFlexPresetLocalId(
            meta?.presetLocalId ?? newPresetDialog.presetLocalId
          );
        }
        if (reason === "cancelled" && newPresetDialog) {
          removeLocalPresetDraft(
            newPresetDialog.cameraId,
            newPresetDialog.presetLocalId
          );
        }
        setNewPresetDialog(null);
      }}
    />
  ) : null;

  return {
    flexIzActive,
    zonesForPreview,
    zonesForColorSidebar,
    ignoreZonesTitleText,
    ignoreZonesHeadingClassName,
    flexPresetSelectControl,
    flexPresetDialog,
    showFlexIgnoreZones,
    selectedFlexPresetId,
  };
}
